import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CalendarDaysIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';

// Classe un événement dans un onglet selon ses dates et son statut
function getEventTab(event) {
  if (event.status === 'brouillon') return 'brouillons';
  if (event.status === 'termine')   return 'passes';

  if (!event.dates?.length) return 'avenir';

  const now = new Date();
  const sorted = [...event.dates].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const last = sorted[sorted.length - 1];
  const lastEnd = last.endDate
    ? new Date(last.endDate)
    : (() => { const d = new Date(last.startDate); d.setHours(23, 59, 59, 999); return d; })();

  return now > lastEnd ? 'passes' : 'avenir';
}

/** Pour l'onglet actifs : distinguer événement déjà commencé vs pas encore (hors brouillon / terminé). */
function getEventTimePhase(event) {
  if (event.status === 'brouillon' || event.status === 'termine') return null;
  if (!event.dates?.length) return 'a_venir';

  const now = new Date();
  const sorted = [...event.dates].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const firstStart = new Date(first.startDate);
  const lastEnd = last.endDate
    ? new Date(last.endDate)
    : (() => { const d = new Date(last.startDate); d.setHours(23, 59, 59, 999); return d; })();

  if (now > lastEnd) return null;
  if (now < firstStart) return 'a_venir';
  return 'en_cours';
}

function firstStartMs(event) {
  if (!event.dates?.length) return Number.MAX_SAFE_INTEGER;
  const sorted = [...event.dates].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  return new Date(sorted[0].startDate).getTime();
}

const STATUS_BADGE = {
  brouillon: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700'   },
  publie:    { label: 'Publié',    cls: 'bg-green-100 text-green-700'  },
  annule:    { label: 'Annulé',   cls: 'bg-red-100 text-red-700'      },
  termine:   { label: 'Terminé',  cls: 'bg-blue-100 text-blue-700'    },
};

export default function AdminEvents() {
  const { organismes } = useOrganisme();
  const navigate   = useNavigate();

  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [activeTab, setActiveTab] = useState('avenir');

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/events`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvents(data);
    } catch (err) {
      console.error('Erreur lors de la récupération des événements:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleDelete = async (eventId, title) => {
    if (!window.confirm(`Supprimer l'événement "${title}" ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/events/${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEvents();
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };


  // Événements avec candidatures en attente (tous onglets confondus)
  const pendingEvents = useMemo(() =>
    events.filter(ev => (ev.volunteerCounts?.pending || 0) > 0),
  [events]);

  // Comptage par onglet (avant filtre texte / organisme)
  const tabCounts = useMemo(() => {
    const counts = { avenir: 0, brouillons: 0, passes: 0 };
    events.forEach(ev => { counts[getEventTab(ev)]++; });
    return counts;
  }, [events]);

  // Liste filtrée
  const eventMatchesOrgFilter = (ev, org) => {
    const concerned = ev.organismesConcernes?.length ? ev.organismesConcernes : (ev.organismes || []);
    const organisers = ev.organismesOrganisateurs?.length ? ev.organismesOrganisateurs : concerned;
    return concerned.includes(org) || organisers.includes(org);
  };

  const displayed = useMemo(() => {
    const filtered = events.filter(ev => {
      if (getEventTab(ev) !== activeTab) return false;
      if (orgFilter && !eventMatchesOrgFilter(ev, orgFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !ev.title.toLowerCase().includes(q) &&
          !(ev.description || '').toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
    if (activeTab !== 'avenir') return filtered;
    return [...filtered].sort((a, b) => {
      const pa = getEventTimePhase(a);
      const pb = getEventTimePhase(b);
      const rank = (p) => (p === 'en_cours' ? 0 : 1);
      const d = rank(pa) - rank(pb);
      if (d !== 0) return d;
      return firstStartMs(a) - firstStartMs(b);
    });
  }, [events, activeTab, search, orgFilter]);

  const TABS = [
    { key: 'avenir',     label: 'En cours / À venir' },
    { key: 'brouillons', label: 'Brouillons' },
    { key: 'passes',     label: 'Passés'     },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Événements</h1>
            <p className="mt-1 text-sm text-gray-500">Gérez les événements de votre organisme</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => navigate('/admin/events/new')}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nouvel événement
            </button>
            <HelpButton
              title={helpContent.evenements.title}
              sections={helpContent.evenements.sections}
            />
          </div>
        </div>

        {/* Onglets */}
        <nav className="mt-5 -mb-px flex gap-1">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === key
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {tabCounts[key]}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* ── Bandeau candidatures en attente ── */}
      {pendingEvents.length > 0 && (
        <div className="mx-8 mt-4 mb-1 rounded-xl border border-yellow-200 bg-yellow-50 px-5 py-3">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-400 text-yellow-900 text-xs font-bold">
              {pendingEvents.reduce((sum, ev) => sum + (ev.volunteerCounts?.pending || 0), 0)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-yellow-900">
                {pendingEvents.length === 1
                  ? 'Un événement a des candidatures en attente'
                  : `${pendingEvents.length} événements ont des candidatures en attente`}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {pendingEvents.map(ev => (
                  <button
                    key={ev._id}
                    onClick={() => navigate(`/admin/events/${ev._id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white border border-yellow-200 text-xs font-medium text-yellow-800 hover:bg-yellow-100 transition-colors"
                  >
                    {ev.title}
                    <span className="px-1.5 py-0.5 rounded-full bg-yellow-200 text-yellow-900 font-bold text-xs">
                      {ev.volunteerCounts.pending}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Filtres ── */}
      <div className="px-8 py-3 bg-white border-b border-gray-100 flex items-center gap-3">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {organismes.length > 1 && (
          <select
            value={orgFilter}
            onChange={e => setOrgFilter(e.target.value)}
            className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Tous les organismes</option>
            {organismes.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
        )}

        {(search || orgFilter) && (
          <button
            onClick={() => { setSearch(''); setOrgFilter(''); }}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Effacer
          </button>
        )}
      </div>

      {/* ── Liste ── */}
      <div className="px-8 py-6">
        <div className="bg-white shadow overflow-hidden rounded-xl">
          <ul className="divide-y divide-gray-100">
            {displayed.length === 0 ? (
              <li className="px-6 py-12 text-center">
                <CalendarDaysIcon className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-3 text-sm font-medium text-gray-900">Aucun événement</p>
                <p className="mt-1 text-sm text-gray-500">
                  {events.length === 0
                    ? 'Commencez par créer votre premier événement.'
                    : 'Aucun événement dans cet onglet.'}
                </p>
              </li>
            ) : (
              displayed.map(event => {
                const badge = STATUS_BADGE[event.status] || STATUS_BADGE.brouillon;
                const firstDate = event.dates?.[0] ? new Date(event.dates[0].startDate) : null;
                const multiDate = (event.dates?.length || 0) > 1;
                const vc = event.volunteerCounts || {};
                return (
                  <li key={event._id}>
                    <div className="flex items-stretch min-h-[80px]">

                      {/* ── Col 1 : date ── */}
                      <div className="w-40 flex-shrink-0 flex flex-col items-center justify-center py-3 px-2">
                        {firstDate ? (
                          <>
                            <span className="text-3xl font-bold text-indigo-700 leading-none">
                              {firstDate.getDate()}
                            </span>
                            <span className="text-xs font-medium text-indigo-500 mt-0.5 text-center capitalize">
                              {firstDate.toLocaleDateString('fr-FR', { month: 'short' })}
                              {' '}{firstDate.getFullYear()}
                            </span>
                            {multiDate && (
                              <span className="mt-1 text-xs text-indigo-400">+{event.dates.length - 1}</span>
                            )}
                          </>
                        ) : (
                          <CalendarDaysIcon className="h-6 w-6 text-indigo-300" />
                        )}
                      </div>

                      {/* ── Col 2 : image ── */}
                      <div className="w-1/6 flex-shrink-0 overflow-hidden py-3 px-2">
                        {event.coverImage?.url ? (
                          <img
                            src={event.coverImage.url}
                            alt={event.title}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <CalendarDaysIcon className="h-7 w-7 text-gray-300" />
                          </div>
                        )}
                      </div>

                      {/* ── Col 3 : infos ── */}
                      <div className="flex-1 px-5 py-3 min-w-0 self-center">

                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-xl font-semibold text-gray-900 truncate">
                            {event.title}
                          </h3>
                          {activeTab === 'avenir' && getEventTimePhase(event) === 'en_cours' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              En cours
                            </span>
                          )}
                        </div>

                        <div className="mt-1.5 flex flex-col flex-wrap items-start gap-x-4 gap-y-1 text-sm text-gray-500">
                          {(event.organismesOrganisateurs?.length > 0 || event.organismes?.length > 0) && (
                            <span className="flex items-start gap-2 flex-wrap">
                              <span className="text-gray-500 flex-shrink-0 pt-0.5">Organisateurs :</span>
                              <span className="flex flex-wrap gap-1">
                                {(event.organismesOrganisateurs?.length
                                  ? event.organismesOrganisateurs
                                  : (event.organismesConcernes?.length ? event.organismesConcernes : event.organismes || [])
                                ).map(org => (
                                  <span key={org} className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                    {org}
                                  </span>
                                ))}
                              </span>
                            </span>
                          )}
                          {event.referents?.length > 0 && (
                            <span className="flex items-center gap-2">
                              <UsersIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              Référent{event.referents?.length > 1 ? 's' : ''} : <strong>{event.referents.map(r => `${r.prenom} ${r.nom}`).join(', ')}</strong>
                            </span>
                          )}
                          <span className="flex items-center gap-2">
                            <UsersIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            Volontaires inscrits : {event.volunteers?.length === 0 && 'Aucun'}
                            {vc.pending > 0 && (
                              <span className="px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-800">
                                {vc.pending} en attente
                              </span>
                            )}
                            {vc.accepted > 0 && (
                              <span className="px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-800">
                                {vc.accepted} accepté{vc.accepted > 1 ? 's' : ''}
                              </span>
                            )}
                            {vc.rejected > 0 && (
                              <span className="px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-800">
                                {vc.rejected} refusé{vc.rejected > 1 ? 's' : ''}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      {/* ── Col 5 : badge ── */}
                      <div className="flex-shrink-0 w-60 flex flex-col items-center justify-center gap-1 px-4 border-l border-gray-100">
                        <span className={`inline-flex items-center px-4 pt-1.5 pb-1 rounded-full text-sm font-medium ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>

                      {/* ── Col 5 : actions ── */}
                      <div className="flex-shrink-0 flex flex-col items-center justify-center gap-1 px-4 border-l border-gray-100">
                        <button
                          onClick={() => navigate(`/admin/events/${event._id}`)}
                          title="Voir"
                          className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/events/${event._id}/edit`)}
                          title="Modifier"
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(event._id, event.title)}
                          title="Supprimer"
                          className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>

                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
